import { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { BestPracticesSetting } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
import { compareBestPracticesMutation } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { useBestPracticesMutation } from 'components/WorkspaceEditor/api/useBestPracticesMutation'
import { updateBestPracticesForContentItem } from 'components/WorkspaceEditor/features/bestPractices'
import {
    useBestPracticeElementData,
    useHoveredItemActions,
    useIsBestPracticeElementDataLoading,
    useIsProjectDataLoading,
    useProjectData,
    useSelectedElementId,
    useSelectedMainMenuNode,
} from 'components/WorkspaceEditor/HoveredItemStore'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import React, { useMemo } from 'react'

/**
 * Represents the complete configuration for a menu item, extending the base MenuItemConfig.
 */
export interface CompleteMenuItemConfig extends MenuItemConfig {
    /** Optional flag indicating whether the menu item is disabled. */
    isDisabled?: boolean
    /** Optional flag indicating whether the menu item is hidden. */
    isHidden?: boolean
    /** Optional small label text for the menu item. */
    smallLabel?: string
}

/** Props for the BestPracticesSettingPopover component. */
export interface BestPracticesSettingPopoverProps {
    /** The element to which the popover is anchored. */
    anchorElement: Element | null
    /** Indicates if the current setting is a best practice. */
    isBestPractice: boolean
    /** Indicates if the current setting is a global best practice. */
    isGlobalBestPractice: boolean
    /** Indicates if editing the best practice setting is disabled. */
    isBestPracticeEditDisabled: boolean
    /** Callback function to close the main menu. */
    onCloseMainMenu?: () => void
}

export const BestPracticesSettingPopover: React.FC<BestPracticesSettingPopoverProps> = ({
    anchorElement,
    isBestPractice,
    isGlobalBestPractice,
    isBestPracticeEditDisabled,
    onCloseMainMenu,
}) => {
    const bestPracticeElementData = useBestPracticeElementData()
    const projectData = useProjectData()
    const isProjectDataLoading = useIsProjectDataLoading()
    const isBestPracticeElementDataLoading = useIsBestPracticeElementDataLoading()
    const { setIsBPMenuDisplayed } = useHoveredItemActions()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const selectedElementId = useSelectedElementId()
    const selectedProjectElementNode = useSelectedProjectElementNode()

    const {
        addUpdateBestPractice,
        removeBestPractice,
    } = useBestPracticesMutation()

    const isLoading = useMemo(() => {
        return isBestPracticeElementDataLoading || isProjectDataLoading
    }, [isBestPracticeElementDataLoading, isProjectDataLoading])

    const handleBestPracticesMutationData = React.useCallback(
        (mutationData: Partial<BestPracticeData> | null) => {
            // Get the latest state from the store for optimistic update
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const contentItemId = selectedMainMenuNode?.id
            // Use selectedElementId from store (set when opening BP menu for SelectionOption pills)
            const elementId = selectedElementId

            if (mutationData) {
                // If there's no existing BP data (getBestPractice is null), we should create a new one
                // Otherwise, check if the mutation data has changed
                const hasExistingBP = bestPracticeElementData?.getBestPractice?.id
                const isMutationUpdated = !hasExistingBP || compareBestPracticesMutation(
                    bestPracticeElementData,
                    projectData?.getProject,
                    mutationData
                )

                if (isMutationUpdated && contentItemId) {
                    const defaultInput = {
                        ...mutationData,
                        parentProjectId: projectData?.getProject?.id,
                        contentItemId,
                        contentItemElementId: elementId, // Add element ID for SelectionOption pills
                    }

                    defaultInput.projectElementNodeId =
                        selectedProjectElementNode?.id

                    const mutationInput =
                        bestPracticeElementData?.getBestPractice?.id &&
                        bestPracticeElementData?.getBestPractice
                            ?.parentProjectId === projectData?.getProject?.id
                            ? {
                                  ...defaultInput,
                                  id:
                                      bestPracticeElementData.getBestPractice
                                          .id,
                              }
                            : defaultInput

                    // Apply optimistic update immediately
                    // If elementId exists, update the element within content item's elements array
                    // Otherwise, update the content item node itself
                    // Logic: If GBP, only set isGlobalBestPractice; if BP, only set isBestPractice
                    const isGlobal = mutationData.isGlobal ?? false
                    const updatedNodes = updateBestPracticesForContentItem(
                        contentItemId,
                        elementId,
                        latestNodes,
                        !isGlobal, // isBestPractice: true only when NOT global
                        isGlobal // isGlobalBestPractice: true only when global
                    )
                    storeState.actions.setContentItemViewNodes(updatedNodes)

                    addUpdateBestPractice({
                        variables: {
                            input: mutationInput,
                        },
                    }).catch((error) => {
                        console.error(
                            'Failed to add/update best practice:',
                            error
                        )
                        // Revert to original state on error
                        storeState.actions.setContentItemViewNodes(latestNodes)
                    })
                }
            } else {
                // Remove best practice
                if (
                    bestPracticeElementData?.getBestPractice?.id &&
                    contentItemId
                ) {
                    // Apply optimistic update immediately (remove BP flags)
                    const updatedNodes = updateBestPracticesForContentItem(
                        contentItemId,
                        elementId,
                        latestNodes,
                        false,
                        false
                    )
                    storeState.actions.setContentItemViewNodes(updatedNodes)

                    removeBestPractice({
                        variables: {
                            input: {
                                id: bestPracticeElementData.getBestPractice.id,
                            },
                        },
                    }).catch((error) => {
                        console.error('Failed to remove best practice:', error)
                        // Revert to original state on error
                        storeState.actions.setContentItemViewNodes(latestNodes)
                    })
                }
            }
            setIsBPMenuDisplayed(false)
            onCloseMainMenu?.()
        },
        [
            bestPracticeElementData,
            projectData,
            selectedMainMenuNode,
            selectedElementId,
            selectedProjectElementNode,
            addUpdateBestPractice,
            removeBestPractice,
            setIsBPMenuDisplayed,
            onCloseMainMenu,
        ]
    )
    return (
        <BestPracticesSetting
            // key={`BP-${template?.id}`}
            popoverAnchor={anchorElement}
            onlyPopover
            projectDetail={projectData?.getProject}
            setBestPracticesMutationData={handleBestPracticesMutationData}
            showBestPracticesIcons={false}
            data={bestPracticeElementData}
            loading={isLoading}
            isBestPractice={isBestPractice}
            isGlobalBestPractice={isGlobalBestPractice}
            isDisabled={isBestPracticeEditDisabled}
        />
    )
}
